import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { Button } from '../ui/button';
import { NotificationBell, ToastContainer } from '../notifications';
import { 
  Activity, FolderKanban, Target, CheckSquare, Users2, Users, 
  Layers, FlaskConical, BarChart3, LogOut, Menu, X, Network, 
  MessageSquare, ChevronLeft, ChevronRight, Sparkles, Settings,
  Brain, Lightbulb, Inbox
} from 'lucide-react';

// Design System Colors
const COLORS = {
  // Sidebar
  sidebarBg: '#0D1117',
  sidebarBgLighter: '#161B22',
  sidebarBorder: 'rgba(255, 255, 255, 0.08)',
  sidebarText: '#E6EDF3',
  sidebarTextMuted: '#8B949E',
  sidebarHover: 'rgba(255, 255, 255, 0.06)',
  sidebarActive: 'rgba(0, 201, 167, 0.15)',
  sidebarActiveBorder: '#00C9A7',
  
  // Brand
  primary: '#00C9A7',
  primaryDark: '#00A88A',
  primaryGlow: 'rgba(0, 201, 167, 0.3)',
  
  // Surface
  surfaceBg: '#F8FAFC',
  surfaceCard: '#FFFFFF',
  surfaceBorder: '#E2E8F0',
  
  // Text
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
};

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, logout, getUserRole, getViewPermissions, authMode } = useAuth();
  const { getUnreadCount } = useNotifications();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const unreadCount = getUnreadCount();
  const currentRole = getUserRole();
  const viewPermissions = getViewPermissions();
  
  // Get display name - prefer userProfile, fallback to user
  const displayName = userProfile?.full_name || user?.name || user?.email?.split('@')[0] || 'User';
  const displayRole = userProfile?.role || currentRole || 'Team Member';
  const displayInitials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const displayRegion = userProfile?.region;
  const displayDepartment = userProfile?.department;

  const navigation = [
    { name: 'PULSE', path: '/pulse', icon: Activity, requiredPermission: null },
    { name: 'PROJECTS', path: '/projects', icon: FolderKanban, requiredPermission: null },
    { name: 'FOCUS', path: '/focus', icon: Target, requiredPermission: null },
    { name: 'TASKS', path: '/tasks', icon: CheckSquare, requiredPermission: null },
    { name: 'MATCH', path: '/match', icon: Brain, requiredPermission: 'canViewAllTeams' },
    { name: 'COLLAB', path: '/collab', icon: Users2, requiredPermission: null },
    { name: 'TEAM', path: '/team', icon: Users, requiredPermission: 'canViewAllTeams' },
    { name: 'STRUCTURE', path: '/structure', icon: Network, requiredPermission: 'canViewStructure' },
    { name: 'THREADS', path: '/threads', icon: MessageSquare, requiredPermission: null },
    { name: 'INBOX', path: '/inbox', icon: Inbox, badge: unreadCount > 0 ? unreadCount : null, requiredPermission: null },
    { name: 'LANES', path: '/lanes', icon: Layers, requiredPermission: 'canViewAllTeams' },
    { name: 'LAB', path: '/lab', icon: FlaskConical, requiredPermission: null },
    { name: 'INSIGHTS', path: '/insights', icon: BarChart3, requiredPermission: 'canViewInsights' },
    { name: 'INTELLIGENCE', path: '/intelligence', icon: Lightbulb, isNew: true, requiredPermission: 'canAccessExecutiveFeatures' },
    { name: 'SETTINGS', path: '/settings', icon: Settings, requiredPermission: null }
  ];
  
  // Filter navigation based on role permissions
  const getFilteredNavigation = () => {
    return navigation.filter(item => {
      // If no permission required, show to everyone
      if (!item.requiredPermission) return true;
      
      // Check if user has the required permission
      return viewPermissions[item.requiredPermission] === true;
    });
  };
  
  const filteredNavigation = getFilteredNavigation();

  // Get role-specific subtitle for sidebar
  const getRoleSubtitle = () => {
    if (displayRole === 'Regional Director' && displayRegion) {
      return `${displayRegion} Region`;
    }
    if (displayRole === 'Team Member' && displayDepartment) {
      return displayDepartment;
    }
    return displayRole;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/projects') {
      return location.pathname === path || location.pathname.startsWith('/projects/');
    }
    return location.pathname === path;
  };

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.surfaceBg }}>
      {/* Premium Dark Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full transition-all duration-300 z-40 ${sidebarOpen ? sidebarWidth : 'w-0'}`}
        style={{ 
          backgroundColor: COLORS.sidebarBg,
          borderRight: `1px solid ${COLORS.sidebarBorder}`
        }}
      >
        <div className={`h-full flex flex-col ${!sidebarOpen && 'hidden'}`}>
          {/* Logo Section */}
          <div 
            className="px-5 py-6 border-b flex items-center justify-between"
            style={{ borderColor: COLORS.sidebarBorder }}
          >
            <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
              {/* Logo Icon with Glow */}
              <div 
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                  boxShadow: `0 0 20px ${COLORS.primaryGlow}`
                }}
              >
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <h1 
                    className="text-lg font-bold tracking-tight"
                    style={{ 
                      color: COLORS.sidebarText,
                      textShadow: `0 0 30px ${COLORS.primaryGlow}`
                    }}
                  >
                    CognitiveOS
                  </h1>
                  <p className="text-[10px] font-medium tracking-wider" style={{ color: COLORS.sidebarTextMuted }}>
                    CREATIVE INTELLIGENCE
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto sidebar-scroll">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium
                    transition-all duration-200 relative
                    ${sidebarCollapsed ? 'justify-center' : ''}
                  `}
                  style={{ 
                    backgroundColor: active ? COLORS.sidebarActive : 'transparent',
                    color: active ? COLORS.primary : COLORS.sidebarTextMuted,
                    borderLeft: active ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                    marginLeft: '-3px',
                    boxShadow: active ? `0 0 20px ${COLORS.primaryGlow}` : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = COLORS.sidebarHover;
                      e.currentTarget.style.color = COLORS.sidebarText;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = COLORS.sidebarTextMuted;
                    }
                  }}
                  title={sidebarCollapsed ? item.name : undefined}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <div className="relative">
                    <Icon 
                      className="w-[18px] h-[18px] flex-shrink-0" 
                      style={{ 
                        filter: active ? `drop-shadow(0 0 6px ${COLORS.primaryGlow})` : 'none'
                      }}
                    />
                    {/* Badge for collapsed sidebar */}
                    {sidebarCollapsed && item.badge && (
                      <span 
                        className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center text-[8px] font-bold text-white rounded-full px-0.5"
                        style={{ backgroundColor: '#EF4444' }}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                  {!sidebarCollapsed && (
                    <span className="flex items-center gap-2 flex-1">
                      {item.name}
                      {item.isNew && (
                        <span 
                          className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{
                            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                            color: 'white',
                            boxShadow: '0 0 8px rgba(245, 158, 11, 0.4)'
                          }}
                        >
                          NEW
                        </span>
                      )}
                      {item.badge && (
                        <span 
                          className="ml-auto text-[10px] min-w-[18px] h-[18px] flex items-center justify-center font-bold text-white rounded-full px-1"
                          style={{ backgroundColor: '#EF4444' }}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Collapse Toggle */}
          <div className="px-3 py-2 border-t" style={{ borderColor: COLORS.sidebarBorder }}>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs transition-all"
              style={{ color: COLORS.sidebarTextMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = COLORS.sidebarHover;
                e.currentTarget.style.color = COLORS.sidebarText;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = COLORS.sidebarTextMuted;
              }}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4" />
                  <span>Collapse</span>
                </>
              )}
            </button>
          </div>

          {/* User Profile Section */}
          <div 
            className="px-4 py-4 border-t"
            style={{ 
              borderColor: COLORS.sidebarBorder,
              backgroundColor: COLORS.sidebarBgLighter
            }}
          >
            <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
              {/* Premium Avatar */}
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold flex-shrink-0"
                style={{ 
                  background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: `0 0 15px ${COLORS.primaryGlow}`
                }}
              >
                {displayInitials || 'U'}
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: COLORS.sidebarText }}>
                    {displayName}
                  </p>
                  <p className="text-xs truncate" style={{ color: COLORS.sidebarTextMuted }}>
                    {getRoleSubtitle()}
                    {authMode === 'demo' && <span className="ml-1 opacity-60">(Demo)</span>}
                  </p>
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-2 mt-3 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                style={{ color: COLORS.sidebarTextMuted }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                  e.currentTarget.style.color = '#EF4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.sidebarTextMuted;
                }}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${sidebarOpen ? (sidebarCollapsed ? 'ml-20' : 'ml-64') : 'ml-0'}`}>
        {/* Premium Header */}
        <header 
          className="sticky top-0 z-30 backdrop-blur-md"
          style={{ 
            background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
            borderBottom: `1px solid ${COLORS.surfaceBorder}`
          }}
        >
          <div className="flex items-center justify-between px-8 py-4">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)} 
              className="p-2.5 rounded-xl transition-all duration-200"
              style={{ 
                color: COLORS.textMuted,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = COLORS.surfaceCard;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            {/* Right side - Date Display and Notification Bell */}
            <div className="flex items-center gap-3">
              {/* Date Display - Premium Style */}
              <div 
                className="flex items-center gap-2 px-4 py-2 rounded-xl"
                style={{ 
                  backgroundColor: COLORS.surfaceCard,
                  border: `1px solid ${COLORS.surfaceBorder}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                }}
              >
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: COLORS.primary }}
                />
                <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
              
              {/* Notification Bell */}
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
      
      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
};

export default MainLayout;
